import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Stack,
  TextField,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Autocomplete,
  useTheme,
  alpha,
} from '@mui/material';
import {
  PictureAsPdfOutlined,
  Groups as GroupsIcon,
  Draw as DrawIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import SearchableSelect from '../../components/common/SearchableSelect';
import { useSessionContext } from '../../context/SessionContext';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import { examService } from '../../services/examService';
import { curriculumApi } from '../../services/sessions/curriculumApi';
import { Session, SessionClass, SectionInstance, CurriculumSubject } from '../../types/sessions';
import examAttendanceSheetService, { ExamAttendanceSheetData } from '../../services/examAttendanceSheetService';
import ExamAttendanceSheetDocument from '../../components/examination/pdf/ExamAttendanceSheetDocument';
import BusyIcon from '../../components/common/BusyIcon';
import { usePdfExport, useSchoolBranding, fmtClassName } from '../../utils/pdf';

/**
 * Exam Attendance Sheet — the printable exam-hall roster (signature sheet).
 *
 * Cascading filters:
 *   Session → Class → Section (optional) → Exam → Subject
 *   + optional Room, Invigilators, and Exam Date (rendered on the PDF header).
 *
 * Preview auto-refreshes on scope change. Print-time metadata (date, room,
 * invigilators) is FE-local so editing those fields doesn't refetch. Download
 * PDF button lives on the top-right, disabled until the preview is loaded.
 */

interface ExamOption {
  _id:       string;
  name:      string;
  startDate: string;
  endDate:   string;
}

interface ExamSubjectOption {
  _id:      string;
  name:     string;
  maxMarks: number;
}

// Trim, drop empties, dedupe (case-insensitive), cap at 6. Used both when
// the Autocomplete's chip array changes and when we auto-commit typed
// text on blur / comma.
const INVIGILATOR_LIMIT = 6;
const normaliseInvigilators = (raw: string[]): string[] =>
  Array.from(new Map(
    raw
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => [s.toLowerCase(), s])
  ).values()).slice(0, INVIGILATOR_LIMIT);

const ExamAttendanceSheetPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { activeSession } = useSessionContext();

  // Reference data
  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes,  setClasses]  = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [exams,    setExams]    = useState<ExamOption[]>([]);
  const [examSubjects, setExamSubjects] = useState<ExamSubjectOption[]>([]);
  // Full curriculum rows for (session, class) — used to derive which
  // section(s) a given subject is taught in so we can auto-select and
  // filter the Section dropdown when the admin picks a subject.
  const [curriculum, setCurriculum] = useState<CurriculumSubject[]>([]);

  // Selection
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedClass,   setSelectedClass]   = useState('');
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [selectedExam,    setSelectedExam]    = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [roomName,        setRoomName]        = useState('');
  // Multiple invigilators — schools often assign 2–3 per hall.
  const [invigilators,    setInvigilators]    = useState<string[]>([]);
  // Uncommitted text inside the Autocomplete input. Kept in state so we
  // can auto-chipify on blur / comma — freeSolo Autocomplete otherwise
  // silently drops whatever the user typed without pressing Enter, which
  // is a classic "why aren't my invigilators on the PDF?" bug.
  const [invigilatorInput, setInvigilatorInput] = useState('');
  // Default the printable date to today (ISO YYYY-MM-DD so the HTML5 date
  // input picks it up). BE re-formats to a friendly display date.
  const [examDate,        setExamDate]        = useState(() => new Date().toISOString().slice(0, 10));

  // Preview
  const [preview, setPreview] = useState<ExamAttendanceSheetData | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  // Default to active session on mount.
  useEffect(() => {
    if (activeSession?._id && !selectedSession) setSelectedSession(activeSession._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession]);

  useEffect(() => {
    (async () => {
      try {
        const [ss, cs] = await Promise.all([sessionsApi.list(), classesApi.list()]);
        setSessions(ss);
        setClasses(cs);
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Failed to load filters', { variant: 'error' });
      }
    })();
  }, [enqueueSnackbar]);

  // Sections + curriculum cascade off (session, class). Curriculum is
  // used later to auto-derive the section for a subject-specific paper.
  useEffect(() => {
    if (!selectedSession || !selectedClass) {
      setSections([]); setCurriculum([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [secs, curr] = await Promise.all([
          sectionInstancesApi.list({ sessionId: selectedSession, classId: selectedClass }),
          curriculumApi.list({ sessionId: selectedSession, classId: selectedClass }),
        ]);
        if (cancelled) return;
        setSections(secs);
        setCurriculum(curr.filter(c => c.isActive));
      } catch {/* silent */}
    })();
    return () => { cancelled = true; };
  }, [selectedSession, selectedClass]);

  // Published exams cascade off (session, class).
  useEffect(() => {
    if (!selectedSession || !selectedClass) { setExams([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await examService.getExams({
          sessionId: selectedSession,
          classId:   selectedClass,
          status:    'published',
          limit:     100,
        });
        if (cancelled) return;
        setExams((res.data?.exams || []).map((e: any) => ({
          _id:       e._id,
          name:      e.name,
          startDate: e.startDate,
          endDate:   e.endDate,
        })));
      } catch {/* silent — empty list means user needs to publish an exam */}
    })();
    return () => { cancelled = true; };
  }, [selectedSession, selectedClass]);

  // Subjects for the chosen exam (from Exam.subjects[]).
  useEffect(() => {
    if (!selectedExam) { setExamSubjects([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await examService.getExamById(selectedExam);
        if (cancelled) return;
        const rows = (res.data?.subjects || []).map((s: any) => {
          const sub = s.subjectId as any;
          return {
            _id:      String(sub?._id ?? sub),
            name:     sub?.name ?? 'Unknown',
            maxMarks: s.maxMarks,
          };
        });
        setExamSubjects(rows);
      } catch {/* silent */}
    })();
    return () => { cancelled = true; };
  }, [selectedExam]);

  // Reset downstream selections when the true scope changes (session or
  // class). Section is intentionally NOT in this list: it's a filter, and
  // it's the auto-select-from-subject effect below that populates it —
  // treating a section change as a scope reset would wipe the exam and
  // subject the admin just picked.
  useEffect(() => {
    setPreview(null); setError(null);
    setSelectedExam(''); setSelectedSubject('');
    setSelectedSection('');
  }, [selectedSession, selectedClass]);

  // Exam change re-fills subjects; wipe subject but keep section (auto-
  // select re-runs when the next subject is picked).
  useEffect(() => {
    setPreview(null); setError(null);
    setSelectedSubject('');
  }, [selectedExam]);

  // NB: Date / Room / Invigilator are pure print-time metadata — they don't
  // change which students appear on the roster. Keeping them out of the
  // fetch deps means editing those fields doesn't spam the API or flash a
  // loading state over the table. They're stamped onto the PDF at
  // download time and previewed from local state below.
  const loadPreview = useCallback(async () => {
    if (!selectedExam || !selectedSubject) return;
    setLoading(true); setError(null);
    try {
      const data = await examAttendanceSheetService.getPreview({
        examId:            selectedExam,
        subjectId:         selectedSubject,
        sectionInstanceId: selectedSection || null,
      });
      setPreview(data);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to load attendance sheet';
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedExam, selectedSubject, selectedSection, enqueueSnackbar]);

  useEffect(() => {
    if (!selectedExam || !selectedSubject) return;
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExam, selectedSubject, selectedSection]);

  // Printed in the browser from the roster on screen and the room, invigilators and date typed in.
  const { generate: generatePdf } = usePdfExport();
  const { branding } = useSchoolBranding();
  const handleDownload = useCallback(async () => {
    if (!preview) return;
    const file = ['attendance-sheet', preview.exam.name, fmtClassName(preview.class.name), preview.section?.name, preview.subject.name]
      .filter(Boolean).join('-').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    setDownloading(true);
    try {
      await generatePdf(
        <ExamAttendanceSheetDocument
          sheet={preview}
          room={roomName.trim() || undefined}
          invigilators={invigilators.map(n => n.trim()).filter(Boolean)}
          examDate={examDate || undefined}
          branding={branding}
          generatedAt={new Date().toISOString()}
        />,
        `${file}.pdf`,
      );
    } finally {
      setDownloading(false);
    }
  }, [preview, roomName, invigilators, examDate, generatePdf, branding]);

  const examOptionsSorted = useMemo(
    () => [...exams].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()),
    [exams]
  );

  // ── Auto-derive the section from the picked subject ────────────────────
  // Curriculum stores subjects as either common (sectionInstanceId = null,
  // taught to everyone in the class) OR section-specific (one row per
  // section that teaches it). We derive the "shape" for the picked subject
  // and use it to (a) narrow the Section dropdown to valid options and
  // (b) pre-fill the section when there's only one valid choice.
  //
  // Soft lock: for a section-specific subject we filter the dropdown down
  // to just the sections that teach it. For common subjects, all sections
  // stay available and the default is "Whole class". Admin can still pick
  // any specific section to print a section-split sheet.
  const subjectScope = useMemo(() => {
    if (!selectedSubject) {
      return { kind: 'unpicked' as const, allowedSectionIds: null as string[] | null };
    }
    const rows = curriculum.filter(c => String(c.subjectId) === String(selectedSubject));
    if (rows.length === 0) {
      // Fallback — subject not in curriculum (shouldn't happen for a subject
      // that appears on the exam, but guards against drift). Behave like
      // common so we don't over-restrict the admin.
      return { kind: 'common' as const, allowedSectionIds: null };
    }
    const hasCommon = rows.some(c => !c.sectionInstanceId);
    if (hasCommon) {
      return { kind: 'common' as const, allowedSectionIds: null };
    }
    const ids = Array.from(new Set(
      rows.map(c => c.sectionInstanceId && String(c.sectionInstanceId))
          .filter((v): v is string => !!v)
    ));
    return { kind: 'section-specific' as const, allowedSectionIds: ids };
  }, [selectedSubject, curriculum]);

  // Options rendered in the Section dropdown — filtered when the picked
  // subject is section-specific, otherwise all sections show.
  const sectionOptions = useMemo(() => {
    const legalSet = subjectScope.allowedSectionIds
      ? new Set(subjectScope.allowedSectionIds)
      : null;
    return sections
      .filter(s => !legalSet || legalSet.has(s._id))
      .map(s => ({ value: s._id, label: s.name }));
  }, [sections, subjectScope]);

  // On subject change: preselect the section when there's exactly one valid
  // option (soft lock — admin can still change it). Clear the selection if
  // it's no longer valid for the new subject.
  useEffect(() => {
    if (subjectScope.kind === 'unpicked') return;
    const legalIds = subjectScope.allowedSectionIds;
    if (legalIds) {
      // Section-specific
      if (legalIds.length === 1) {
        if (selectedSection !== legalIds[0]) setSelectedSection(legalIds[0]);
      } else if (selectedSection && !legalIds.includes(selectedSection)) {
        setSelectedSection('');
      }
    } else {
      // Common subject — default to whole class if the previously-picked
      // section isn't legal (it always is for common, but wipe if the
      // section list itself has shrunk).
      if (selectedSection && !sections.some(s => s._id === selectedSection)) {
        setSelectedSection('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectScope]);

  const canDownload = Boolean(preview) && !downloading;
  const showEmpty   = !loading && !preview && !error;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Attendance Sheets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Printable exam-hall roster — students sign against their names when they sit the paper.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<BusyIcon busy={downloading}><PictureAsPdfOutlined fontSize="small" /></BusyIcon>}
          onClick={handleDownload}
          disabled={!canDownload}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderColor: theme.palette.divider,
            color: theme.palette.text.secondary,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              color: theme.palette.primary.main,
            },
            '&.Mui-disabled': { borderColor: theme.palette.divider },
          }}
        >
          {downloading ? 'Preparing…' : 'Download PDF'}
        </Button>
      </Box>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          flexWrap="wrap"
          useFlexGap
        >
          <SearchableSelect
            label="Session"
            value={selectedSession}
            onChange={setSelectedSession}
            options={sessions.map(s => ({
              value: s._id,
              label: s.name,
              badge: s.status === 'active'
                ? <Chip label="Active" size="small" color="success" sx={{ height: 18, fontSize: 10 }} />
                : undefined,
            }))}
            placeholder="Session"
            clearable={false}
            minWidth={180}
          />

          <SearchableSelect
            label="Class"
            value={selectedClass}
            onChange={setSelectedClass}
            options={classes.map(c => ({ value: c._id, label: c.name }))}
            placeholder="Class"
            disabled={!selectedSession}
            clearable={false}
            minWidth={140}
          />

          <SearchableSelect
            label="Section"
            value={selectedSection}
            onChange={setSelectedSection}
            options={sectionOptions}
            placeholder="Whole class"
            // Section-specific subject with one legal section auto-fills.
            // Disable the picker when there's no real choice to make
            // (a single legal section or no sections at all).
            disabled={
              !selectedClass ||
              sectionOptions.length === 0 ||
              (subjectScope.kind === 'section-specific' && sectionOptions.length === 1)
            }
            minWidth={160}
          />

          <SearchableSelect
            label="Exam"
            value={selectedExam}
            onChange={setSelectedExam}
            options={examOptionsSorted.map(e => ({
              value: e._id,
              label: e.name,
              secondary: format(new Date(e.startDate), 'dd MMM yyyy'),
            }))}
            placeholder="Pick an exam"
            disabled={!selectedClass || examOptionsSorted.length === 0}
            clearable={false}
            minWidth={220}
          />

          <SearchableSelect
            label="Subject"
            value={selectedSubject}
            onChange={setSelectedSubject}
            options={examSubjects.map(s => ({
              value: s._id,
              label: s.name,
              secondary: `Max ${s.maxMarks}`,
            }))}
            placeholder="Pick a subject"
            disabled={!selectedExam || examSubjects.length === 0}
            clearable={false}
            minWidth={200}
          />

          {loading && (
            <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'center', color: 'text.secondary', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Loading…</Typography>
            </Box>
          )}
        </Stack>

        {/* Print-time metadata (Date, Room, Invigilator) — only relevant once
            everything else is picked; keeps the primary row clean. */}
        {selectedExam && selectedSubject && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}
          >
            <TextField
              label="Exam Date"
              size="small"
              type="date"
              value={examDate}
              onChange={(e) => setExamDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 170 }}
            />
            <TextField
              label="Room (optional)"
              size="small"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              placeholder="e.g. Hall B"
              sx={{ flex: 1, maxWidth: 220 }}
            />
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={[] as string[]}
              value={invigilators}
              inputValue={invigilatorInput}
              onChange={(_, v) => setInvigilators(normaliseInvigilators(v.map(String)))}
              // Auto-chipify: any comma in the input splits and commits.
              // Prevents the "typed names never made it to the PDF because
              // I forgot to press Enter" bug.
              onInputChange={(_, next, reason) => {
                if (reason === 'reset') { setInvigilatorInput(''); return; }
                if (next.includes(',')) {
                  const parts = next.split(',');
                  const toAdd = parts.slice(0, -1);
                  const rest  = parts[parts.length - 1];
                  setInvigilators(prev => normaliseInvigilators([...prev, ...toAdd]));
                  setInvigilatorInput(rest);
                  return;
                }
                setInvigilatorInput(next);
              }}
              renderTags={(value, getTagProps) =>
                value.map((name, index) => {
                  const { key, ...tagProps } = getTagProps({ index }) as any;
                  return <Chip key={key} label={name} size="small" {...tagProps} />;
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Invigilators (optional)"
                  placeholder="Type a name, press Enter (or comma)"
                  // Commit whatever's left in the input when the field loses
                  // focus — download would otherwise silently drop it.
                  onBlur={() => {
                    if (invigilatorInput.trim()) {
                      setInvigilators(prev => normaliseInvigilators([...prev, invigilatorInput]));
                      setInvigilatorInput('');
                    }
                  }}
                />
              )}
              sx={{ flex: 1, minWidth: 240, maxWidth: 380 }}
            />
            <Typography variant="caption" color="text.disabled" sx={{ alignSelf: 'center', ml: { sm: 1 } }}>
              These show on the printed sheet header.
            </Typography>
          </Stack>
        )}
      </Paper>

      {/* States */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Skeleton variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={30} sx={{ mb: 1, borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={30} sx={{ mb: 1, borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={30} sx={{ borderRadius: 1 }} />
        </Paper>
      )}

      {showEmpty && (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
          <DrawIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            Pick a session, class, exam, and subject — the roster loads automatically.
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            Every enrolled student appears with a blank signature box, sorted by roll number ascending.
          </Typography>
        </Paper>
      )}

      {!loading && preview && (
        <PreviewPanel
          data={preview}
          theme={theme}
          examDate={examDate}
          room={roomName}
          invigilators={invigilators}
        />
      )}
    </Box>
  );
};

// Human-friendly date for the meta chip; falls back to the raw value if
// the string can't be parsed (defensive — BE echoes whatever we sent).
const formatExamDate = (v: string): string => {
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  try {
    return format(d, 'EEE, dd MMM yyyy');
  } catch {
    return v;
  }
};

// ── Preview panel ──────────────────────────────────────────────────────────

const PreviewPanel: React.FC<{
  data: ExamAttendanceSheetData;
  theme: any;
  // Print-time metadata comes from local state, not the fetched payload —
  // that way the admin sees their edits reflected live without a refetch.
  examDate?:    string;
  room?:        string;
  invigilators?: string[];
}> = ({ data, theme, examDate, room, invigilators }) => {
  const headerBg = theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f8f9fa';
  return (
    <Box>
      {/* Meta strip — mirrors what will print on the PDF header, so admin
          verifies before hitting Download. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {data.exam.name} · {data.subject.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data.class.name}{data.section ? ` · ${data.section.name}` : ''}  ·  {data.session.name}  ·  Max Marks {data.subject.maxMarks}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              icon={<GroupsIcon fontSize="small" />}
              label={`${data.students.length} student${data.students.length === 1 ? '' : 's'}`}
              sx={{ fontWeight: 600 }}
            />
            {examDate && (
              <Chip label={`Date: ${formatExamDate(examDate)}`} variant="outlined" />
            )}
            {room && <Chip label={`Room: ${room}`} variant="outlined" />}
            {invigilators && invigilators.length > 0 && (
              <Chip
                label={`${invigilators.length > 1 ? 'Invigilators' : 'Invigilator'}: ${invigilators.join(', ')}`}
                variant="outlined"
              />
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Roster preview */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 640 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}`, width: 60 }} align="center">S.No</TableCell>
                <TableCell sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}`, width: 80 }} align="center">Roll</TableCell>
                <TableCell sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }}>Student</TableCell>
                {!data.section && (
                  <TableCell sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}`, width: 140 }}>Section</TableCell>
                )}
                <TableCell sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}` }} align="center">Signature</TableCell>
                <TableCell sx={{ fontWeight: 700, background: headerBg, borderBottom: `1px solid ${theme.palette.divider}`, width: 180 }} align="center">Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.students.map((s, i) => (
                <TableRow
                  key={s.studentId}
                  hover
                  sx={{ '&:hover': { background: alpha(theme.palette.primary.main, 0.03) } }}
                >
                  <TableCell align="center" sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>{s.rollNumber}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{s.name}</TableCell>
                  {!data.section && (
                    <TableCell sx={{ color: 'text.secondary' }}>{s.sectionName || '—'}</TableCell>
                  )}
                  <TableCell align="center" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.75rem' }}>
                    {/* Signature is blank on print — preview just hints at the space. */}
                    blank on print
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.75rem' }}>
                    blank on print
                  </TableCell>
                </TableRow>
              ))}
              {data.students.length === 0 && (
                <TableRow>
                  <TableCell colSpan={99} align="center" sx={{ py: 5, color: 'text.disabled' }}>
                    No enrolled students in this scope.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ExamAttendanceSheetPage;
